/* SwipeableKindCard — mobile-first swipe-to-action wrapper.
   Swipe right → onSwipeRight (aanmelden)
   Swipe left  → onSwipeLeft  (afmelden)
   Both zijn optioneel; als een handler ontbreekt, is die richting uitgeschakeld. */
import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { LogIn, LogOut } from "lucide-react";

const THRESHOLD = 80; // px waarna een swipe telt
const MAX_DRAG = 140; // maximum zichtbare drag

export default function SwipeableKindCard({
  onSwipeRight,
  onSwipeLeft,
  swipeRightLabel = "Aanmelden",
  swipeLeftLabel = "Afmelden",
  disabled = false,
  children,
  className = "",
  ...rest
}) {
  const startX = useRef(null);
  const isMoving = useRef(false);
  const [dx, setDx] = useState(0);
  const [firing, setFiring] = useState(null); // "left" | "right" | null

  const onStart = (clientX) => {
    if (disabled) return;
    startX.current = clientX;
    isMoving.current = true;
  };
  const onMove = (clientX) => {
    if (!isMoving.current || startX.current == null) return;
    let d = clientX - startX.current;
    // Snap volgens beschikbare handlers
    if (d > 0 && !onSwipeRight) d = 0;
    if (d < 0 && !onSwipeLeft) d = 0;
    if (d > MAX_DRAG) d = MAX_DRAG;
    if (d < -MAX_DRAG) d = -MAX_DRAG;
    setDx(d);
  };
  const onEnd = () => {
    if (!isMoving.current) return;
    const d = dx;
    isMoving.current = false;
    startX.current = null;
    if (d >= THRESHOLD && onSwipeRight) {
      setFiring("right");
      setTimeout(() => { onSwipeRight(); setDx(0); setFiring(null); }, 120);
    } else if (d <= -THRESHOLD && onSwipeLeft) {
      setFiring("left");
      setTimeout(() => { onSwipeLeft(); setDx(0); setFiring(null); }, 120);
    } else {
      setDx(0);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-md" data-testid="swipeable-kind-card">
      {/* Achtergrond-actie labels */}
      {onSwipeRight && (
        <div className={`absolute inset-y-0 left-0 flex items-center px-4 text-white font-semibold text-sm bg-emerald-600 transition-opacity ${dx > 20 ? "opacity-100" : "opacity-0"}`} data-testid="swipe-right-hint">
          <LogIn className="h-5 w-5 mr-1.5" /> {swipeRightLabel}
        </div>
      )}
      {onSwipeLeft && (
        <div className={`absolute inset-y-0 right-0 flex items-center px-4 text-white font-semibold text-sm bg-[color:var(--terracotta)] transition-opacity ${dx < -20 ? "opacity-100" : "opacity-0"}`} data-testid="swipe-left-hint">
          {swipeLeftLabel} <LogOut className="h-5 w-5 ml-1.5" />
        </div>
      )}
      <Card
        className={`relative bg-white shadow-none ${className}`}
        style={{
          transform: `translateX(${dx}px)`,
          transition: isMoving.current ? "none" : "transform 200ms ease-out",
          touchAction: "pan-y",
        }}
        onTouchStart={e => onStart(e.touches[0].clientX)}
        onTouchMove={e => onMove(e.touches[0].clientX)}
        onTouchEnd={onEnd}
        onTouchCancel={() => { setDx(0); isMoving.current = false; }}
        onPointerDown={e => { if (e.pointerType !== "touch") onStart(e.clientX); }}
        onPointerMove={e => { if (e.pointerType !== "touch") onMove(e.clientX); }}
        onPointerUp={e => { if (e.pointerType !== "touch") onEnd(); }}
        data-firing={firing || undefined}
        {...rest}
      >
        {children}
      </Card>
    </div>
  );
}
